const swatchSelector = '.suit-shop-static-swatch';
const initializedAttribute = 'data-preview-initialized';
const previewTokens = new WeakMap();
const warmedUrls = new Set();

/** Download + decode a preview once; later clicks reuse the browser cache. */
function warmPreview(url) {
  if (!url || warmedUrls.has(url)) return;
  warmedUrls.add(url);
  const img = new Image();
  img.decoding = 'async';
  img.fetchPriority = 'low';
  img.src = url;
  img.decode?.().catch(() => {});
}

function warmCard(card) {
  card.querySelectorAll(`${swatchSelector}[data-preview-image-url]`).forEach((swatch) => {
    warmPreview(swatch.dataset.previewImageUrl);
  });
}

// Warm all color previews of a card shortly before it scrolls into view.
const cardObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            cardObserver.unobserve(entry.target);
            const run = () => warmCard(entry.target);
            'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 2000 }) : setTimeout(run, 300);
          });
        },
        { rootMargin: '300px' }
      )
    : null;

function getCardImage(swatch) {
  return swatch.closest('article')?.querySelector('img') ?? null;
}

function getCardLinks(swatch) {
  return (
    swatch
      .closest('article')
      ?.querySelectorAll('.new-arrivals-card__link, .best-sellers-card__link, .ss-look-card__link') ?? []
  );
}

function updateCardLink(swatch) {
  const variantId = swatch.dataset.variantId;
  if (!variantId) return;

  getCardLinks(swatch).forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.href);
    url.searchParams.set('variant', variantId);
    link.href = url.toString();
  });
}

function updateSelectedSwatch(swatch) {
  const swatchGroup = swatch.closest('.suit-shop-static-card-swatches');
  if (!swatchGroup) return;

  swatchGroup.querySelectorAll('.suit-shop-static-swatch').forEach((item) => {
    const selected = item === swatch;
    item.dataset.selected = selected ? 'true' : 'false';
    item.setAttribute('aria-pressed', selected ? 'true' : 'false');
  });
}

function updateCardImage(swatch) {
  const previewUrl = swatch.dataset.previewImageUrl;
  const image = getCardImage(swatch);
  if (!previewUrl || !image) return;

  const token = (previewTokens.get(image) ?? 0) + 1;
  previewTokens.set(image, token);

  warmedUrls.add(previewUrl);
  const preloadedImage = new Image();
  preloadedImage.src = previewUrl;
  preloadedImage.decode().catch(() => {}).then(() => {
    if (previewTokens.get(image) !== token || swatch.dataset.selected !== 'true') return;
    image.src = previewUrl;
    image.srcset = previewUrl;
    image.removeAttribute('sizes');
  });
}

function selectSwatch(swatch) {
  if (swatch.matches(':disabled')) return;
  updateSelectedSwatch(swatch);
  updateCardLink(swatch);
  updateCardImage(swatch);
}

function initializeSwatches(root = document) {
  root.querySelectorAll(swatchSelector).forEach((swatch) => {
    if (!(swatch instanceof HTMLElement) || swatch.hasAttribute(initializedAttribute)) return;

    swatch.setAttribute(initializedAttribute, 'true');

    const card = swatch.closest('article');
    if (card && cardObserver && !card.hasAttribute('data-swatch-observed')) {
      card.setAttribute('data-swatch-observed', 'true');
      cardObserver.observe(card);
    }

    // Start downloading as soon as a finger/pointer reaches the color.
    const warm = () => warmPreview(swatch.dataset.previewImageUrl);
    swatch.addEventListener('pointerenter', warm, { passive: true });
    swatch.addEventListener('pointerdown', warm, { passive: true });
    swatch.addEventListener('touchstart', warm, { passive: true });
    swatch.addEventListener('focus', warm);

    swatch.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      selectSwatch(swatch);
    });

    if (swatch.dataset.selected === 'true') updateCardLink(swatch);
  });
}

initializeSwatches();
document.addEventListener('shopify:section:load', (event) => initializeSwatches(event.target));
